import HttpService from '../Service/HttpService'
import { getElementChild, getElementChildren } from '../Utility/Dom'
import { IS_ACTIVE, IS_HIDDEN, IS_LOADING } from '../Utility/State'

export interface BaseTeaserItem {
  category: Category | null
  detailLink: string
  slug: string
  teaserImageData: TeaserImageData
  teaserImageVariants: TeaserImageVariants
  teaserText: string
  teaserTitle: string
  title: string
  uid: number
}

export interface Category {
  color: string
  title: string
  uid: number
}

export interface HydraResponse<T> {
  'hydra:member': T[]
  'hydra:totalItems': number
  'hydra:view'?: {
    'hydra:next'?: string
  }
}

export interface TeaserImageData {
  alternative: null | string
  description: null | string
  title: null | string
}

export interface TeaserImageVariants {
  desktop379x300: null | string
  desktop379x300_2x: null | string
  mobile256x128: null | string
  mobile256x128_2x: null | string
  mobile282x141: null | string
  mobile282x141_2x: null | string
  mobile470x235: null | string
  mobile470x235_2x: null | string
}

export interface TeaserListConfig {
  apiUrl: string
  containerSelector: string
  itemsPerPage?: number
  listSelector: string
  templateSelector: string
}

export abstract class AbstractTeaserList<T extends BaseTeaserItem> {
  protected readonly apiUrl: string
  protected readonly container: HTMLElement
  protected currentCategory: null | number = null
  protected currentPage = 1
  protected readonly filterButtons: [] | NodeListOf<HTMLElement>
  protected isLoading = false
  protected readonly itemsPerPage: number
  protected readonly list: HTMLElement | null
  protected readonly loadingBar: HTMLElement | null
  protected readonly loadingBarButton: HTMLElement | null

  protected readonly loadingBarEnd: HTMLElement | null
  protected readonly loadingBarProgress: HTMLElement | null

  protected readonly loadingBarStart: HTMLElement | null
  protected readonly pageIds: number[]
  protected readonly template: HTMLTemplateElement | null
  protected totalItems = 0

  constructor(container: HTMLElement, config: TeaserListConfig) {
    this.container = container
    this.apiUrl = config.apiUrl ?? ''
    this.itemsPerPage = config.itemsPerPage ?? 6

    this.pageIds = container.dataset.items
      ?.split(',')
      .map((id: string): number => parseInt(id.trim(), 10))
      .filter((id: number): boolean => !isNaN(id)) ?? []

    this.list = getElementChild(this.container, config.listSelector)
    const filterBar = getElementChild(this.container, 'filter-list')
    this.filterButtons = getElementChildren(filterBar, 'filter-button')
    this.loadingBar = getElementChild(this.container, 'loading-bar')
    this.loadingBarButton = getElementChild(this.loadingBar, 'loading-bar-button')
    this.loadingBarStart = getElementChild(this.loadingBar, 'loading-bar-start')
    this.loadingBarEnd = getElementChild(this.loadingBar, 'loading-bar-end')
    this.loadingBarProgress = getElementChild(this.loadingBar, 'loading-bar-progress')
    this.template = getElementChild(this.container, config.templateSelector) as HTMLTemplateElement | null

    this.addFilterEvents()
    this.addLoadMoreEvent()

    if (this.loadingBarEnd !== null) {
      this.totalItems = parseInt(this.loadingBarEnd.textContent ?? '0', 10)
      this.updateLoadingBar()
    }
  }

  protected abstract createItem(item: T): HTMLElement | null

  protected async loadItems(replaceItems: boolean): Promise<void> {
    if (this.isLoading === true || this.list === null) {
      return
    }

    this.isLoading = true
    this.container.classList.add(IS_LOADING)
    this.loadingBarButton?.classList.add(IS_LOADING)

    if (replaceItems === true) {
      this.list.innerHTML = ''
    }

    const url = this.buildApiUrl()

    try {
      const response: HydraResponse<T> = await HttpService.get(url)

      this.renderItems(response['hydra:member'])
      this.totalItems = response['hydra:totalItems']

      const hasMoreItems = response['hydra:view']?.['hydra:next'] !== undefined
      this.updateLoadingBar(this.totalItems, hasMoreItems)
    } catch (error) {
      console.error('Error loading items:', error)
    } finally {
      this.isLoading = false
      this.container.classList.remove(IS_LOADING)
      this.loadingBarButton?.classList.remove(IS_LOADING)
    }
  }

  protected truncateText(text: string, maxLength: number): string {
    if (text === null || text === undefined) {
      return ''
    }

    const strippedText = text.replace(/<[^>]*>/g, '')

    if (strippedText.length <= maxLength) {
      return strippedText
    }

    return strippedText.substring(0, maxLength).trim() + '…'
  }

  protected updatePictureSources(picture: HTMLElement, item: T): void {
    const sources = picture.querySelectorAll('source')
    const img = picture.querySelector('img')
    const variants = item.teaserImageVariants
    const imageData = item.teaserImageData

    sources.forEach((source: HTMLSourceElement): void => {
      const media = source.getAttribute('media')

      if (media === '(min-width: 1440px)' && variants.desktop379x300) {
        source.srcset = `${variants.desktop379x300} 1x${variants.desktop379x300_2x ? `, ${variants.desktop379x300_2x} 2x` : ''}`
      } else if (media === '(min-width: 360px)' && variants.mobile470x235) {
        source.srcset = `${variants.mobile470x235} 1x${variants.mobile470x235_2x ? `, ${variants.mobile470x235_2x} 2x` : ''}`
      } else if (media === '(min-width: 321px)' && variants.mobile282x141) {
        source.srcset = `${variants.mobile282x141} 1x${variants.mobile282x141_2x ? `, ${variants.mobile282x141_2x} 2x` : ''}`
      } else if ((media === null || media === '') && variants.mobile256x128) {
        source.srcset = `${variants.mobile256x128} 1x${variants.mobile256x128_2x ? `, ${variants.mobile256x128_2x} 2x` : ''}`
      }
    })

    if (img !== null && variants.desktop379x300) {
      img.src = variants.desktop379x300
      img.srcset = `${variants.desktop379x300} 1x${variants.desktop379x300_2x ? `, ${variants.desktop379x300_2x} 2x` : ''}`
      img.alt = imageData?.alternative || item.teaserTitle || item.title
    } else {
      picture.remove()
    }
  }

  private addFilterEvents(): void {
    if (this.filterButtons.length === 0) {
      return
    }

    this.filterButtons.forEach((button: HTMLElement): void => {
      button.addEventListener('click', (): void => {
        const categoryValue = button.dataset.value

        this.filterButtons?.forEach((btn: HTMLElement): void => {
          btn.classList.remove(IS_ACTIVE)
        })

        button.classList.add(IS_ACTIVE)

        if (categoryValue === '' || categoryValue === '0' || categoryValue === undefined) {
          this.currentCategory = null
        } else {
          this.currentCategory = parseInt(categoryValue, 10)
        }

        this.currentPage = 1
        this.loadItems(true)
      })
    })
  }

  private addLoadMoreEvent(): void {
    if (this.loadingBarButton === null) {
      return
    }

    this.loadingBarButton.addEventListener('click', (): void => {
      if (this.isLoading === true) {
        return
      }

      this.currentPage++
      this.loadItems(false)
    })
  }

  private buildApiUrl(): string {
    const params = new URLSearchParams()

    params.set('page', this.currentPage.toString())
    params.set('itemsPerPage', this.itemsPerPage.toString())

    if (this.currentCategory !== null) {
      params.set('category', this.currentCategory.toString())
    }

    this.pageIds.forEach((pid: number): void => {
      params.append('pid[]', pid.toString())
    })

    return `${this.apiUrl}?${params.toString()}`
  }

  private renderItems(items: T[]): void {
    if (this.list === null || this.template === null) {
      return
    }

    items.forEach((item: T): void => {
      const element = this.createItem(item)

      if (element !== null) {
        this.list?.appendChild(element)
      }
    })
  }

  private updateLoadingBar(totalItems?: number, hasMoreItems?: boolean): void {
    if (this.loadingBar === null) {
      return
    }

    const total = totalItems ?? this.totalItems
    const currentCount = this.list?.children.length ?? 0

    if (this.loadingBarStart !== null) {
      this.loadingBarStart.textContent = currentCount.toString()
    }

    if (this.loadingBarEnd !== null) {
      this.loadingBarEnd.textContent = total.toString()
    }

    if (this.loadingBarProgress !== null) {
      const progress = total > 0 ? (currentCount / total) * 100 : 0
      this.loadingBarProgress.setAttribute('style', `--progress:${progress}%`)
    }

    const allItemsLoaded = hasMoreItems === false || currentCount >= total

    if (allItemsLoaded) {
      this.loadingBarButton?.classList.add(IS_HIDDEN)
    } else {
      this.loadingBarButton?.classList.remove(IS_HIDDEN)
    }
  }
}
